import React from 'react';
import { services } from '../data/mock';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Services = () => {
  const navigate = useNavigate();

  return (
    <section id="services" className="relative py-32 lg:py-40 bg-[#020617] overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyMTIsIDE3NSwgNTUsIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 animate-grid-scroll" />
      
      {/* Enhanced Floating Particles - More Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[18%] right-[20%] w-2.5 h-2.5 bg-[#D4AF37]/55 rounded-full animate-particle-float-smooth-1 blur-sm" />
        <div className="absolute top-[42%] left-[22%] w-2 h-2 bg-white/50 rounded-full animate-particle-float-smooth-2 blur-sm" />
        <div className="absolute bottom-[28%] right-[30%] w-1.5 h-1.5 bg-[#D4AF37]/45 rounded-full animate-particle-float-smooth-3 blur-sm" />
        <div className="absolute top-[65%] left-[45%] w-2 h-2 bg-white/40 rounded-full animate-particle-float-smooth-4 blur-sm" />
        <div className="absolute top-[32%] right-[45%] w-1.5 h-1.5 bg-[#D4AF37]/50 rounded-full animate-particle-float-smooth-5 blur-sm" />
        <div className="absolute bottom-[48%] left-[35%] w-1 h-1 bg-white/45 rounded-full animate-particle-float-smooth-6 blur-sm" />
        <div className="absolute top-[55%] right-[25%] w-1.5 h-1.5 bg-[#D4AF37]/55 rounded-full animate-particle-float-smooth-7 blur-sm" />
        <div className="absolute bottom-[38%] left-[55%] w-2 h-2 bg-white/35 rounded-full animate-particle-float-smooth-8 blur-sm" />
        <div className="absolute top-[25%] left-[60%] w-1 h-1 bg-[#D4AF37]/60 rounded-full animate-particle-float-smooth-9 blur-sm" />
        <div className="absolute bottom-[15%] right-[55%] w-1.5 h-1.5 bg-white/50 rounded-full animate-particle-float-smooth-10 blur-sm" />
      </div>

      {/* Sweeping Light - Enhanced */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/8 to-transparent animate-light-sweep-smooth" />
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-[#D4AF37]/4 to-transparent animate-gradient-wave-services" />
      
      {/* Smooth Light Beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[35%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/18 to-transparent animate-beam-services-1" />
        <div className="absolute top-0 right-[40%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/12 to-transparent animate-beam-services-2" />
      </div>
      
      {/* Pulsing Ambient Orbs */}
      <div className="absolute top-1/5 right-1/6 w-80 h-80 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse-ambient-services-1" />
      <div className="absolute bottom-1/4 left-1/5 w-72 h-72 bg-[#D4AF37]/8 rounded-full blur-3xl animate-pulse-ambient-services-2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <div className="mb-20 animate-header-reveal">
          <div className="flex items-center gap-6 mb-8">
            <div className="w-16 h-[1px] bg-[#D4AF37] animate-line-grow" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-light animate-text-glow-gentle">Investment Solutions</span>
          </div>
          <div className="grid lg:grid-cols-3 gap-12 items-end mb-16">
            <div className="lg:col-span-2">
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-tight animate-title-slide-in">
                Strategic Opportunities Across Global Markets
              </h2>
              <p className="text-white text-lg font-light leading-relaxed">
                Diversified investment strategies designed to deliver exceptional risk-adjusted returns in high-growth sectors. Our portfolio spans seven key investment verticals, each carefully selected for their growth potential and alignment with our core values.
              </p>
            </div>
            <div className="animate-fade-in animation-delay-800">
              <div className="bg-[#0f172a]/60 backdrop-blur-md border border-white/10 p-6">
                <div className="text-[#D4AF37] font-serif text-4xl font-light mb-2">7</div>
                <div className="text-[#94A3B8] text-sm font-light">Investment Verticals</div>
                <div className="w-full h-[1px] bg-[#D4AF37]/30 my-4" />
                <div className="text-white text-xs font-light leading-relaxed">Each sector strategically chosen for sustainable growth and value creation</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, idx) => (
            <div
              key={service.id}
              className={`group relative overflow-hidden bg-[#0f172a] border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-700 cursor-pointer hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#D4AF37]/20 animate-card-reveal ${
                idx === 0 ? 'md:col-span-2 lg:col-span-2' : ''
              } ${idx === 3 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              onClick={() => navigate(`/services/${service.id}`)}
              style={{ animationDelay: `${idx * 150}ms` }}
            >
              {/* Image with Zoom Effect */}
              <div className="relative h-80 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/60 to-transparent" />
                
                {/* Animated Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 via-[#D4AF37]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                {/* Shimmer */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                
                {/* Service Number with Glow */}
                <div className="absolute top-6 right-6 text-7xl font-serif text-white/10 group-hover:text-[#D4AF37]/20 transition-all duration-700 animate-number-pulse">
                  0{service.id}
                </div>
              </div>

              {/* Content */}
              <div className="p-8 lg:p-10 relative">
                {/* Animated Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                <h3 className="text-2xl md:text-3xl font-serif text-white mb-4 tracking-tight group-hover:text-[#D4AF37] transition-colors duration-500 relative z-10">
                  {service.title}
                </h3>
                <p className="text-[#94A3B8] mb-6 line-clamp-3 font-light leading-relaxed group-hover:text-white/80 transition-colors duration-500 relative z-10">
                  {service.description}
                </p>
                
                {/* Learn More with Icon Animation */}
                <div className="flex items-center gap-3 text-[#D4AF37] group-hover:gap-6 transition-all duration-500 relative z-10">
                  <span className="text-sm uppercase tracking-[0.2em] font-light">Learn More</span>
                  <ArrowRight className="w-4 h-4 stroke-[1px] transition-transform duration-500 group-hover:translate-x-2" />
                </div>
              </div>

              {/* Animated Border */}
              <div className="absolute bottom-0 left-0 w-0 h-[3px] bg-gradient-to-r from-[#D4AF37] to-[#D4AF37]/50 group-hover:w-full transition-all duration-700" />
              
              {/* Corner Accent */}
              <div className="absolute top-0 right-0 w-0 h-0 border-t-[40px] border-t-transparent border-r-[40px] border-r-[#D4AF37]/0 group-hover:border-r-[#D4AF37]/20 transition-all duration-700" />
            </div>
          ))}
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes grid-scroll {
          0% { transform: translateY(0) translateX(0); }
          100% { transform: translateY(80px) translateX(80px); }
        }

        @keyframes particle-float-smooth-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(60px, -80px) scale(1.35); opacity: 0.85; }
        }

        @keyframes particle-float-smooth-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(-70px, 90px); opacity: 0.8; }
        }

        @keyframes particle-float-smooth-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.45; }
          50% { transform: translate(50px, -60px) rotate(180deg); opacity: 0.75; }
        }

        @keyframes particle-float-smooth-4 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate(-60px, -70px) scale(1.3); opacity: 0.7; }
        }

        @keyframes particle-float-smooth-5 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(65px, 75px); opacity: 0.8; }
        }

        @keyframes particle-float-smooth-6 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.45; }
          50% { transform: translate(-55px, -65px) rotate(200deg); opacity: 0.75; }
        }

        @keyframes particle-float-smooth-7 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(70px, -80px) scale(1.4); opacity: 0.85; }
        }

        @keyframes particle-float-smooth-8 {
          0%, 100% { transform: translate(0, 0); opacity: 0.35; }
          50% { transform: translate(-68px, 78px); opacity: 0.65; }
        }

        @keyframes particle-float-smooth-9 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.6; }
          50% { transform: translate(58px, 68px) rotate(180deg); opacity: 0.85; }
        }

        @keyframes particle-float-smooth-10 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(-62px, -72px) scale(1.35); opacity: 0.8; }
        }

        @keyframes light-sweep-smooth {
          0% { transform: translateX(-100%); opacity: 0; }
          30% { opacity: 0.08; }
          70% { opacity: 0.08; }
          100% { transform: translateX(200%); opacity: 0; }
        }

        @keyframes gradient-wave-services {
          0%, 100% { transform: translateY(0); opacity: 0.04; }
          50% { transform: translateY(-30px); opacity: 0.10; }
        }

        @keyframes beam-services-1 {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 0.18; transform: translateY(100%); }
        }

        @keyframes beam-services-2 {
          0%, 100% { opacity: 0; transform: translateY(100%); }
          50% { opacity: 0.12; transform: translateY(-100%); }
        }

        @keyframes pulse-ambient-services-1 {
          0%, 100% { opacity: 0.10; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.35); }
        }

        @keyframes pulse-ambient-services-2 {
          0%, 100% { opacity: 0.08; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.4); }
        }

        @keyframes header-reveal {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes line-grow {
          0% { width: 0; opacity: 0; }
          100% { width: 4rem; opacity: 1; }
        }

        @keyframes text-glow-gentle {
          0%, 100% { text-shadow: 0 0 12px rgba(212, 175, 55, 0.25); }
          50% { text-shadow: 0 0 24px rgba(212, 175, 55, 0.45); }
        }

        @keyframes title-slide-in {
          0% { opacity: 0; transform: translateX(-30px); filter: blur(5px); }
          100% { opacity: 1; transform: translateX(0); filter: blur(0); }
        }

        @keyframes card-reveal {
          0% { opacity: 0; transform: translateY(40px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes number-pulse {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.05); }
        }

        .animate-grid-scroll { animation: grid-scroll 35s linear infinite; }
        .animate-particle-float-smooth-1 { animation: particle-float-smooth-1 22s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-2 { animation: particle-float-smooth-2 24s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-3 { animation: particle-float-smooth-3 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-4 { animation: particle-float-smooth-4 28s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-5 { animation: particle-float-smooth-5 25s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-6 { animation: particle-float-smooth-6 27s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-7 { animation: particle-float-smooth-7 29s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-8 { animation: particle-float-smooth-8 23s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-9 { animation: particle-float-smooth-9 30s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-particle-float-smooth-10 { animation: particle-float-smooth-10 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-light-sweep-smooth { animation: light-sweep-smooth 18s ease-in-out infinite; }
        .animate-gradient-wave-services { animation: gradient-wave-services 20s ease-in-out infinite; }
        .animate-beam-services-1 { animation: beam-services-1 19s ease-in-out infinite; }
        .animate-beam-services-2 { animation: beam-services-2 21s ease-in-out infinite; }
        .animate-pulse-ambient-services-1 { animation: pulse-ambient-services-1 13s ease-in-out infinite; }
        .animate-pulse-ambient-services-2 { animation: pulse-ambient-services-2 15s ease-in-out infinite; }
        .animate-header-reveal { animation: header-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-line-grow { animation: line-grow 0.8s ease-out forwards; }
        .animate-text-glow-gentle { animation: text-glow-gentle 4s ease-in-out infinite; }
        .animate-title-slide-in { animation: title-slide-in 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-card-reveal { animation: card-reveal 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-number-pulse { animation: number-pulse 5s ease-in-out infinite; }
      `}</style>
    </section>
  );
};

export default Services;
